import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portalApi, tournamentApi } from '../../services/api'
import PortalShell from '../setup/PortalShell'
import MultiCoursePartnersStep from './MultiCoursePartnersStep'
import MultiCourseRulesStep from './MultiCourseRulesStep'
import Toggle from '../../components/Toggle'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Stay & Play Package Manager
 *
 * v59 model: packages are for a FIXED number of golfers (1-4) with a
 * flat total price (not per-person). The package defines what gets booked:
 *   - NumGolfers (1 to 8)
 *   - Rounds (each golfer gets this many rounds; 1-10, quick-pick 1/2/3 + Custom)
 *   - Nights (hotel stay length; 1-14, quick-pick 1/2/3 + Custom)
 *   - Price (total for the whole package)
 *
 * BedType is still stored on the package (backend defaults to 'double' for
 * Expedia hotel search) but not exposed in the editor UI.
 *
 * At checkout, the customer picks a package and pays the listed price.
 * No "how many golfers?" question — the package already defines it.
 */

const TIME_RANGES = [
  { value: 'any', label: 'Any time' },
  { value: 'early_morning', label: 'Early morning (6-9am)' },
  { value: 'late_morning', label: 'Late morning (9am-12pm)' },
  { value: 'afternoon', label: 'Afternoon (12-5pm)' },
  { value: 'twilight', label: 'Twilight (after 5pm)' },
  { value: 'custom', label: 'Custom hours' },
]

const EMPTY_MULTI_RULES = {
  ForceOnePerPartner: 0,
  MaxRoundPrice: null,
  MinAdvanceDays: null,
  NoConsecutiveSameCourse: 0,
  BlackoutDates: [],
  FreeCancellationDays: null,
  DayRestriction: 'any',
}

const EMPTY_PACKAGE = {
  PackageName: '',
  Description: '',
  NumGolfers: 1,
  Rounds: 1,
  Nights: 1,
  HotelRooms: 1,
  BedType: 'double',
  Price: '',
  MaxHotelBudget: 150,
  MinStarRating: '',
  TeeTimeRange: 'any',
  TeeTimeFrom: '',
  TeeTimeTo: '',
  HeroImageUrl: '',
  IsActive: 1,
  ValidFrom: '',
  ValidTo: '',
  inclusions: [],
  // Plan 463: split + installment payments (per-package toggles)
  EnableSplitPayments: 0,
  EnableInstallments: 0,
  MaxInstallments: 3,
  SecondaryPaymentDeadlineHours: 48,
  // Plan 498 feature 6: repeat-customer loyalty
  EnableLoyaltyDiscount: 0,
  LoyaltyDiscountPct: 10,
  LoyaltyMinPriorTrips: 1,
}

export default function PackageManager() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // null = list view, 'new' = creating, or numeric ID = editing
  const [editing, setEditing] = useState(null)
  // Creation-flow gate: null = choose scope, 'single' = this course only, 'multi' = multi-course flow
  // Existing packages skip the gate (treated as 'single' on load).
  const [packageScope, setPackageScope] = useState(null)
  // Multi-course sub-step: 'partners' -> 'rules' -> 'form' (main package fields)
  const [multiStep, setMultiStep] = useState('partners')
  const [multiPartners, setMultiPartners] = useState([])
  const [multiRules, setMultiRules] = useState(EMPTY_MULTI_RULES)
  const [pkgForm, setPkgForm] = useState(EMPTY_PACKAGE)
  const [saving, setSaving] = useState(false)
  const [newInclusion, setNewInclusion] = useState('')

  useEffect(() => {
    loadPackages()
    // Load the course's commission rate for the cost breakdown
    portalApi.get('/onboarding/status')
      .then(({ data }) => {
        const pct = data.settings?.PlayStayCommissionPct
        if (pct != null && Number(pct) > 0) setCommissionPct(Number(pct))
      })
      .catch(() => {})
  }, [])

  async function loadPackages() {
    setLoading(true)
    setError('')
    try {
      const { data } = await tournamentApi.get('/play-stay/packages')
      setPackages(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load packages')
    } finally {
      setLoading(false)
    }
  }

  // One-click seed: creates the 3 starter packages (Weekend Getaway,
  // Executive, Premium Golf Retreat). Server refuses if the catalog isn't
  // empty, so this can never silently duplicate over real packages.
  const [seeding, setSeeding] = useState(false)
  async function seedDefaults() {
    if (!confirm(t('sp_seed_defaults_confirm', 'Create 3 starter packages (Weekend Getaway, Executive, Premium Golf Retreat)? You can edit or delete them after.'))) return
    setSeeding(true)
    setError('')
    try {
      await tournamentApi.post('/play-stay/packages/seed-defaults')
      await loadPackages()
    } catch (err) {
      setError(err.response?.data?.message || t('sp_seed_failed', 'Failed to seed packages'))
    } finally {
      setSeeding(false)
    }
  }

  function startNew() {
    setPkgForm({ ...EMPTY_PACKAGE, inclusions: [], addons: [] })
    setPackageScope(null) // show the scope gate before the form
    setMultiStep('partners')
    setMultiPartners([])
    setMultiRules(EMPTY_MULTI_RULES)
    setEditing('new')
  }

  function cancelEditing() {
    setEditing(null)
    setPackageScope(null)
    setMultiStep('partners')
    setMultiPartners([])
    setMultiRules(EMPTY_MULTI_RULES)
    setError('')
  }

  function startEdit(pkg) {
    setPkgForm({
      ...EMPTY_PACKAGE,
      ...pkg,
      NumGolfers: pkg.NumGolfers || 1,
      Rounds: pkg.Rounds || pkg.RoundsPerGolfer || 1,
      Nights: pkg.Nights || 1,
      HotelRooms: pkg.HotelRooms || 1,
      Price: pkg.Price || pkg.PricePerPerson || '',
      BedType: pkg.BedType || 'double',
      MinStarRating: pkg.MinStarRating || '',
      ValidFrom: pkg.ValidFrom ? String(pkg.ValidFrom).substring(0, 10) : '',
      ValidTo: pkg.ValidTo ? String(pkg.ValidTo).substring(0, 10) : '',
      TeeTimeFrom: pkg.TeeTimeFrom || '',
      TeeTimeTo: pkg.TeeTimeTo || '',
      inclusions: pkg.inclusions || [],
      EnableSplitPayments: pkg.EnableSplitPayments ? 1 : 0,
      EnableInstallments: pkg.EnableInstallments ? 1 : 0,
      MaxInstallments: pkg.MaxInstallments || 3,
      SecondaryPaymentDeadlineHours: pkg.SecondaryPaymentDeadlineHours || 48,
      EnableLoyaltyDiscount: pkg.EnableLoyaltyDiscount ? 1 : 0,
      LoyaltyDiscountPct: pkg.LoyaltyDiscountPct != null ? Number(pkg.LoyaltyDiscountPct) : 10,
      LoyaltyMinPriorTrips: pkg.LoyaltyMinPriorTrips || 1,
      addons: [], // loaded async below for existing packages
    })

    // Lazy-load add-ons for existing packages (Feature 2)
    if (pkg.ID_NO) {
      tournamentApi.get(`/play-stay/packages/${pkg.ID_NO}/addons`)
        .then(({ data }) => setPkgForm(f => ({ ...f, addons: Array.isArray(data) ? data.map(a => ({
          Name: a.Name,
          Description: a.Description,
          PriceCents: Number(a.PriceCents),
          PriceMode: a.PriceMode,
          MaxQuantity: a.MaxQuantity,
          IsActive: a.IsActive ? 1 : 0,
          SortOrder: a.SortOrder,
        })) : [] })))
        .catch(() => {})
    }

    if (pkg.IsMultiCourse) {
      // Multi-course package: hydrate partners + rules, drop the editor straight into the form step
      setMultiPartners(Array.isArray(pkg.partners) ? pkg.partners : [])
      // Hydrate v63 rule fields. BlackoutDates may come back as a JSON string or array.
      let blackout = pkg.BlackoutDates
      if (typeof blackout === 'string') {
        try { blackout = JSON.parse(blackout) } catch { blackout = [] }
      }
      if (!Array.isArray(blackout)) blackout = []
      setMultiRules({
        ForceOnePerPartner: pkg.ForceOnePerPartner ? 1 : 0,
        MaxRoundPrice: pkg.MaxRoundPrice != null ? Number(pkg.MaxRoundPrice) : null,
        MinAdvanceDays: pkg.MinAdvanceDays != null ? Number(pkg.MinAdvanceDays) : null,
        NoConsecutiveSameCourse: pkg.NoConsecutiveSameCourse ? 1 : 0,
        BlackoutDates: blackout,
        FreeCancellationDays: pkg.FreeCancellationDays != null ? Number(pkg.FreeCancellationDays) : null,
        DayRestriction: pkg.DayRestriction || 'any',
      })
      setPackageScope('multi')
      setMultiStep('form')
    } else {
      setPackageScope('single')
    }
    setEditing(pkg.ID_NO)
  }

  function setField(key, val) {
    setPkgForm(f => ({ ...f, [key]: val }))
  }

  function addInclusion() {
    const name = newInclusion.trim()
    if (!name) return
    setPkgForm(f => ({ ...f, inclusions: [...(f.inclusions || []), { ItemName: name }] }))
    setNewInclusion('')
  }

  function removeInclusion(idx) {
    setPkgForm(f => ({ ...f, inclusions: (f.inclusions || []).filter((_, i) => i !== idx) }))
  }

  async function handleSave() {
    if (!pkgForm.PackageName.trim()) {
      setError('Package name is required')
      return
    }
    if (!pkgForm.Price || Number(pkgForm.Price) <= 0) {
      setError('Package price must be greater than 0')
      return
    }

    const isMulti = packageScope === 'multi'
    const roundsVal = Math.min(Math.max(Number(pkgForm.Rounds) || 1, 1), 10)

    // Cross-validation for multi-course packages
    if (isMulti) {
      if (multiPartners.length < 2) {
        setError('Multi-course packages need at least 2 partner courses')
        return
      }
      if (multiRules.ForceOnePerPartner && roundsVal < multiPartners.length) {
        setError(
          `Rule "require one round at each partner" needs at least ${multiPartners.length} rounds (you have ${roundsVal}). ` +
          `Increase the round count or remove some partners.`
        )
        return
      }
    }

    setError('')
    setSaving(true)
    try {
      const payload = {
        ...pkgForm,
        Price: Number(pkgForm.Price),
        NumGolfers: Math.min(Math.max(Number(pkgForm.NumGolfers) || 1, 1), 8),
        Rounds: roundsVal,
        Nights: Math.min(Math.max(Number(pkgForm.Nights) || 1, 1), 14),
        HotelRooms: Math.min(Math.max(Number(pkgForm.HotelRooms) || 1, 1), 10),
        MaxHotelBudget: Number(pkgForm.MaxHotelBudget) || 150,
        MinStarRating: pkgForm.MinStarRating ? Number(pkgForm.MinStarRating) : null,
        IsActive: pkgForm.IsActive ? 1 : 0,
        IsMultiCourse: isMulti ? 1 : 0,
        ForceOnePerPartner: isMulti && multiRules.ForceOnePerPartner ? 1 : 0,
        MaxRoundPrice: isMulti && multiRules.MaxRoundPrice != null ? Number(multiRules.MaxRoundPrice) : null,
        MinAdvanceDays: isMulti && multiRules.MinAdvanceDays != null ? Number(multiRules.MinAdvanceDays) : null,
        NoConsecutiveSameCourse: isMulti && multiRules.NoConsecutiveSameCourse ? 1 : 0,
        BlackoutDates: isMulti ? (multiRules.BlackoutDates || []) : [],
        FreeCancellationDays: isMulti && multiRules.FreeCancellationDays != null ? Number(multiRules.FreeCancellationDays) : null,
        DayRestriction: isMulti ? (multiRules.DayRestriction || 'any') : 'any',
        partners: isMulti
          ? multiPartners.map((p, i) => ({ Courses_ID: p.ID_NO, SortOrder: i }))
          : [],
        // Plan 463 payment flexibility
        EnableSplitPayments: pkgForm.EnableSplitPayments ? 1 : 0,
        EnableInstallments: pkgForm.EnableInstallments ? 1 : 0,
        MaxInstallments: Math.min(Math.max(Number(pkgForm.MaxInstallments) || 3, 2), 12),
        SecondaryPaymentDeadlineHours: Math.min(Math.max(Number(pkgForm.SecondaryPaymentDeadlineHours) || 48, 24), 168),
        // Plan 498 feature 6: loyalty
        EnableLoyaltyDiscount: pkgForm.EnableLoyaltyDiscount ? 1 : 0,
        LoyaltyDiscountPct: Math.min(Math.max(Number(pkgForm.LoyaltyDiscountPct) || 10, 0), 100),
        LoyaltyMinPriorTrips: Math.min(Math.max(Number(pkgForm.LoyaltyMinPriorTrips) || 1, 1), 10),
      }
      let savedId
      if (editing === 'new') {
        const { data } = await tournamentApi.post('/play-stay/packages', payload)
        savedId = data?.id || data?.ID_NO
      } else {
        await tournamentApi.put(`/play-stay/packages/${editing}`, payload)
        savedId = editing
      }
      // Sync add-ons menu (Feature 2). Replaces the entire menu — empty array clears it.
      if (savedId && Array.isArray(pkgForm.addons)) {
        try {
          await tournamentApi.put(`/play-stay/packages/${savedId}/addons`, {
            addons: pkgForm.addons.map((a, i) => ({
              Name: a.Name,
              Description: a.Description || null,
              PriceCents: Math.max(0, Number(a.PriceCents) || 0),
              PriceMode: a.PriceMode || 'flat',
              MaxQuantity: Math.min(Math.max(Number(a.MaxQuantity) || 1, 1), 99),
              IsActive: a.IsActive === 0 ? 0 : 1,
              SortOrder: i,
            })),
          })
        } catch (err) {
          console.warn('Add-on sync failed (package saved but menu may be stale):', err)
        }
      }
      cancelEditing()
      await loadPackages()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save package')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(pkg) {
    if (!confirm(`${t('sp_delete_pkg_confirm_pre', 'Delete')} "${pkg.PackageName}"? ${t('sp_cannot_be_undone', 'This cannot be undone.')}`)) return
    try {
      await tournamentApi.delete(`/play-stay/packages/${pkg.ID_NO}`)
      await loadPackages()
    } catch (err) {
      setError(err.response?.data?.message || t('sp_delete_pkg_failed', 'Failed to delete package'))
    }
  }

  // Duplicate: server-side clone of package + partners + inclusions.
  // New package is created inactive so the merchant can tweak price/dates
  // before it goes live. After creation, auto-open it in the editor.
  async function handleDuplicate(pkg) {
    try {
      const { data } = await tournamentApi.post(`/play-stay/packages/${pkg.ID_NO}/duplicate`)
      await loadPackages()
      // Pull the fresh list and open the new package for editing
      const { data: freshPkgs } = await tournamentApi.get('/play-stay/packages')
      const newPkg = Array.isArray(freshPkgs) ? freshPkgs.find(p => p.ID_NO === data.id) : null
      if (newPkg) startEdit(newPkg)
    } catch (err) {
      setError(err.response?.data?.message || t('sp_duplicate_pkg_failed', 'Failed to duplicate package'))
    }
  }

  async function toggleActive(pkg) {
    try {
      await tournamentApi.put(`/play-stay/packages/${pkg.ID_NO}`, {
        ...pkg,
        Price: pkg.Price || pkg.PricePerPerson,
        NumGolfers: pkg.NumGolfers || 1,
        Rounds: pkg.Rounds || pkg.RoundsPerGolfer || 1,
        IsActive: pkg.IsActive ? 0 : 1,
      })
      await loadPackages()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update package')
    }
  }

  // Hotel availability check — debounced call to Expedia via the backend
  const [hotelCheck, setHotelCheck] = useState(null) // { available, total, message, ... }
  const [hotelChecking, setHotelChecking] = useState(false)
  const hotelCheckTimer = useRef(null)

  // Re-check whenever the relevant fields change while editing
  useEffect(() => {
    if (editing === null) return
    const budget = Number(pkgForm.MaxHotelBudget)
    const nights = Number(pkgForm.Nights)
    if (!budget || !nights) { setHotelCheck(null); return }

    clearTimeout(hotelCheckTimer.current)
    hotelCheckTimer.current = setTimeout(async () => {
      setHotelChecking(true)
      try {
        const params = { budget, nights }
        const { data } = await tournamentApi.get('/play-stay/hotel-count', { params })
        setHotelCheck(data)
      } catch {
        setHotelCheck({ available: 0, message: 'Could not check hotel availability.' })
      } finally {
        setHotelChecking(false)
      }
    }, 800) // debounce 800ms

    return () => clearTimeout(hotelCheckTimer.current)
  }, [editing, pkgForm.MaxHotelBudget, pkgForm.Nights])

  // Commission rate — loaded from the API alongside packages. Defaults to 5%
  // if neither the course nor the system has a value set. The admin section to
  // change this per-course will be built later; for now it reads the existing
  // PlayStayCommissionPct from the course settings or the system_settings default.
  const [commissionPct, setCommissionPct] = useState(5)

  // Derived summary for the editor
  const totalRounds = (Number(pkgForm.NumGolfers) || 1) * (Number(pkgForm.Rounds) || 1)
  const perPerson = Number(pkgForm.Price) > 0 && Number(pkgForm.NumGolfers) > 0
    ? (Number(pkgForm.Price) / Number(pkgForm.NumGolfers)).toFixed(2)
    : null

  return (
    <PortalShell>
      <div style={{ maxWidth: 1100, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
              {t('sp_stay_and_play_caps', 'STAY & PLAY')}
            </div>
            <h1 style={{ margin: 0, fontSize: 28, color: 'var(--secondary-950)' }}>{t('sp_nav_packages', 'Packages')}</h1>
            <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
              {t('sp_packages_subtitle', 'Each package is built for 1 to 8 golfers with a fixed price, hotel rooms, and tee times.')}
            </p>
          </div>
          {editing === null && (
            <button onClick={startNew} className="btn btn-primary" style={{ padding: '12px 20px', whiteSpace: 'nowrap' }}>
              {t('sp_new_package_btn', '+ New Package')}
            </button>
          )}
        </div>

        {error && <div style={errorBoxStyle}>{error}</div>}

        {loading && <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading_ellipsis', 'Loading...')}</div>}

        {/* ── List view ── */}
        {!loading && editing === null && (
          packages.length === 0 ? (
            <div className="card" style={{ padding: 60, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }} aria-hidden="true">⛳</div>
              <h3 style={{ margin: '0 0 8px', color: 'var(--secondary-950)' }}>{t('sp_no_packages_yet', 'No packages yet')}</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>
                {t('sp_no_packages_blurb', 'Build your own from scratch, or start with our 3 ready-made templates.')}
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={seedDefaults}
                  disabled={seeding}
                  className="btn btn-primary"
                  style={{ padding: '12px 24px' }}
                >
                  {seeding ? t('sp_creating', 'Creating...') : t('sp_create_3_starter_packages', 'Create 3 starter packages')}
                </button>
                <button onClick={startNew} className="btn" style={{ padding: '12px 24px' }}>
                  {t('sp_build_my_own', 'Build my own')}
                </button>
              </div>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 16 }}>
                {t('sp_starter_packages_blurb', 'Starter packages: Weekend Getaway · Executive · Premium Golf Retreat. Edit anything after.')}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {packages.map(pkg => {
                const ng = pkg.NumGolfers || 1
                const rpg = pkg.Rounds || pkg.RoundsPerGolfer || 1
                const golferLabel = ng === 1 ? t('sp_golfer_solo', 'Solo') : ng === 2 ? t('sp_golfer_duo', 'Duo') : ng === 3 ? t('sp_golfer_trio', 'Trio') : t('sp_golfer_foursome', 'Foursome')
                const price = pkg.Price || pkg.PricePerPerson || 0
                return (
                  <div key={pkg.ID_NO} className="card" style={{ padding: 20, opacity: pkg.IsActive ? 1 : 0.55 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <h3 style={{ margin: 0, fontSize: 18, color: 'var(--secondary-950)' }}>{pkg.PackageName}</h3>
                          {pkg.IsMultiCourse ? (
                            <span style={multiCourseBadge}>{t('sp_multi_course_caps', 'MULTI-COURSE')}</span>
                          ) : (
                            <span style={golferBadge}>{golferLabel}</span>
                          )}
                          {!pkg.IsActive && <span style={inactiveBadge}>{t('sp_inactive_caps', 'INACTIVE')}</span>}
                        </div>
                        {pkg.Description && (
                          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                            {pkg.Description}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: 16, fontSize: 13, flexWrap: 'wrap' }}>
                          <span><strong>${Number(price).toFixed(2)}</strong> {t('sp_total_lower', 'total')}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{ng} {ng !== 1 ? t('sp_golfers_lower', 'golfers') : t('sp_golfer_lower', 'golfer')}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{rpg} {rpg !== 1 ? t('sp_rounds_each_plural', 'rounds each') : t('sp_round_each_singular', 'round each')}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{pkg.Nights} {pkg.Nights !== 1 ? t('sp_nights_lower', 'nights') : t('sp_night_lower', 'night')}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{t('sp_hotel_max_per_night', 'Hotel ≤')} ${Number(pkg.MaxHotelBudget).toFixed(0)}/{t('sp_night_lower', 'night')}</span>
                        </div>
                        {pkg.inclusions?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                            {pkg.inclusions.map((inc, i) => (
                              <span key={i} style={inclusionPill}>{inc.ItemName}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => startEdit(pkg)} style={smallBtnStyle}>{t('edit', 'Edit')}</button>
                        <button onClick={() => handleDuplicate(pkg)} style={smallBtnStyle}>{t('sp_duplicate', 'Duplicate')}</button>
                        <button onClick={() => toggleActive(pkg)} style={smallBtnStyle}>
                          {pkg.IsActive ? t('sp_deactivate', 'Deactivate') : t('sp_activate', 'Activate')}
                        </button>
                        <button onClick={() => handleDelete(pkg)} style={{ ...smallBtnStyle, color: '#dc2626', borderColor: '#fecaca' }}>
                          {t('delete', 'Delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        )}

        {/* ── Creation gate: pick single-course vs multi-course ── */}
        {!loading && editing === 'new' && packageScope === null && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, color: 'var(--secondary-950)' }}>{t('sp_new_package_h', 'New package')}</h2>
                <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
                  {t('sp_new_package_question', 'Is this a package for your course only, or a multi-course package?')}
                </p>
              </div>
              <button onClick={cancelEditing} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
                {t('sp_back_to_list', '← Back to list')}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Single-course card */}
              <button type="button"
                onClick={() => setPackageScope('single')}
                style={{
                  padding: 24, textAlign: 'left',
                  border: '2px solid var(--card-border)', borderRadius: 12,
                  background: '#fff', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-500)'; e.currentTarget.style.background = 'var(--brand-50)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.background = '#fff' }}
              >
                <div style={{ fontSize: 36, marginBottom: 10 }}>🏌️</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 6 }}>
                  {t('sp_this_course_only', 'This course only')}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {t('sp_this_course_only_blurb', 'All rounds play at your course. You own the package, set the price, and keep the margin after InteGolf\'s cut.')}
                </div>
              </button>

              {/* Multi-course card */}
              <button type="button"
                onClick={() => setPackageScope('multi')}
                style={{
                  padding: 24, textAlign: 'left',
                  border: '2px solid var(--card-border)', borderRadius: 12,
                  background: '#fff', cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-500)'; e.currentTarget.style.background = 'var(--brand-50)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.background = '#fff' }}
              >
                <div style={{ fontSize: 36, marginBottom: 10 }}>🗺️</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 6 }}>
                  {t('sp_multi_course_package', 'Multi-course package')}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  {t('sp_multi_course_blurb', 'Rounds split across multiple partnered courses. Great for regional tours or buddy trips.')}
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ── Multi-course: Partners step (creation flow OR edit-existing via recap "Edit ✎") ── */}
        {!loading && editing !== null && packageScope === 'multi' && multiStep === 'partners' && (
          <MultiCoursePartnersStep
            initialPartners={multiPartners}
            // New package: back goes to scope gate; existing package: back returns to the main form
            onBack={() => editing === 'new' ? setPackageScope(null) : setMultiStep('form')}
            // New package: proceed to rules; existing package: apply and return to form
            onNext={(partners) => {
              setMultiPartners(partners)
              setMultiStep(editing === 'new' ? 'rules' : 'form')
            }}
            backLabel={editing === 'new' ? undefined : '← Back to package details'}
            nextLabel={editing === 'new' ? undefined : 'Apply partner changes →'}
          />
        )}

        {/* ── Multi-course: Rules step (creation flow OR edit-existing via recap "Edit ✎") ── */}
        {!loading && editing !== null && packageScope === 'multi' && multiStep === 'rules' && (
          <MultiCourseRulesStep
            partners={multiPartners}
            initialRules={multiRules}
            onBack={() => editing === 'new' ? setMultiStep('partners') : setMultiStep('form')}
            onNext={(rules) => {
              setMultiRules(rules)
              setMultiStep('form')
            }}
            backLabel={editing === 'new' ? undefined : '← Back to package details'}
            nextLabel={editing === 'new' ? undefined : 'Apply rule changes →'}
          />
        )}

        {/* ── Editor (single-course AND multi-course form step) ── */}
        {!loading && editing !== null && (packageScope === 'single' || (packageScope === 'multi' && multiStep === 'form')) && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 22, color: 'var(--secondary-950)' }}>
                {editing === 'new' ? t('sp_new_package_h', 'New package') : t('sp_edit_package_h', 'Edit package')}
                {packageScope === 'multi' && (
                  <span style={{
                    marginLeft: 12, fontSize: 12, fontWeight: 700, letterSpacing: 1,
                    background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: 10, verticalAlign: 'middle',
                  }}>
                    {t('sp_multi_course_caps', 'MULTI-COURSE')}
                  </span>
                )}
              </h2>
              <div style={{ display: 'flex', gap: 8 }}>
                {packageScope === 'multi' && editing === 'new' && (
                  <button onClick={() => setMultiStep('rules')} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
                    {t('sp_back_to_rules', '← Back to rules')}
                  </button>
                )}
                <button onClick={cancelEditing} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
                  {t('sp_back_to_list', '← Back to list')}
                </button>
              </div>
            </div>

            {/* Multi-course recap banner — partners + rules */}
            {packageScope === 'multi' && (
              <div style={{
                padding: 16, background: '#f9fafb', border: '1px solid var(--card-border)',
                borderRadius: 10, marginBottom: 20,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        {t('sp_partners_count', 'Partners')} ({multiPartners.length})
                      </div>
                      <button type="button"
                        onClick={() => setMultiStep('partners')}
                        style={recapEditBtn}>
                        {t('sp_edit_pencil', 'Edit ✎')}
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {multiPartners.map(p => (
                        <span key={p.ID_NO} style={{
                          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 12,
                          background: 'var(--brand-50)', color: 'var(--brand-700)',
                          border: '1px solid var(--brand-500)',
                        }}>
                          {p.CourseName}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        {t('sp_rules', 'Rules')}
                      </div>
                      <button type="button"
                        onClick={() => setMultiStep('rules')}
                        style={recapEditBtn}>
                        {t('sp_edit_pencil', 'Edit ✎')}
                      </button>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--secondary-950)', lineHeight: 1.6 }}>
                      <div>
                        {multiRules.ForceOnePerPartner ? t('sp_one_round_each_partner', '✓ One round at each partner') : t('sp_free_distribution', '○ Free distribution')}
                      </div>
                      <div>
                        {multiRules.MaxRoundPrice ? `${t('sp_max_label', 'Max')} $${Number(multiRules.MaxRoundPrice).toFixed(0)}/${t('sp_round_lower', 'round')}` : t('sp_no_per_round_cap', 'No per-round price cap')}
                      </div>
                      {multiRules.NoConsecutiveSameCourse ? <div>{t('sp_no_consec_same_course_check', '✓ No consecutive days at same course')}</div> : null}
                      {multiRules.DayRestriction && multiRules.DayRestriction !== 'any' ? (
                        <div>{t('sp_days_label', 'Days:')} {multiRules.DayRestriction === 'weekday' ? t('sp_weekdays_only', 'Weekdays only') : t('sp_weekends_only', 'Weekends only')}</div>
                      ) : null}
                      {multiRules.MinAdvanceDays != null ? <div>{t('sp_min_advance_short', 'Min')} {multiRules.MinAdvanceDays} {multiRules.MinAdvanceDays !== 1 ? t('sp_days_lower', 'days') : t('sp_day_lower', 'day')} {t('sp_advance_lower', 'advance')}</div> : null}
                      {multiRules.FreeCancellationDays != null ? (
                        <div>{t('sp_free_cancel_up_to', 'Free cancel up to')} {multiRules.FreeCancellationDays} {multiRules.FreeCancellationDays !== 1 ? t('sp_days_lower', 'days') : t('sp_day_lower', 'day')} {t('sp_before_lower', 'before')}</div>
                      ) : <div style={{ color: 'var(--text-muted)' }}>{t('sp_non_refundable', 'Non-refundable')}</div>}
                      {Array.isArray(multiRules.BlackoutDates) && multiRules.BlackoutDates.length > 0 ? (
                        <div>{multiRules.BlackoutDates.length} {multiRules.BlackoutDates.length !== 1 ? t('sp_blackout_dates_plural', 'blackout dates') : t('sp_blackout_date_singular', 'blackout date')}</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Package name + description */}
            <h3 style={sectionHeaderStyle}>{t('sp_basics', 'Basics')}</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabelStyle}>{t('sp_package_name', 'Package name')} <Req /></label>
              <input type="text" value={pkgForm.PackageName} onChange={e => setField('PackageName', e.target.value)}
                placeholder={t('sp_package_name_placeholder', 'e.g. Weekend Escape, Executive Stay & Play')}
                style={textInputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={fieldLabelStyle}>{t('sp_description', 'Description')}</label>
              <textarea value={pkgForm.Description || ''} onChange={e => setField('Description', e.target.value)}
                placeholder={t('sp_description_placeholder', 'What makes this package special?')}
                rows={3}
                style={{ ...textInputStyle, resize: 'vertical', minHeight: 70 }} />
            </div>

            {/* ── The key questions — asked in the right order ── */}
            <h3 style={sectionHeaderStyle}>{t('sp_who_is_package_for', 'Who is this package for?')}</h3>

            {/* Q1: How many golfers? */}
            <div style={{ marginBottom: 16 }}>
              <label style={fieldLabelStyle}>{t('sp_how_many_golfers', 'How many golfers?')}</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {[
                  { n: 1, icon: '🧑', label: t('sp_golfer_solo', 'Solo'), sub: t('sp_golfer_count_1', '1 golfer, 1 hotel room') },
                  { n: 2, icon: '🧑🧑', label: t('sp_golfer_duo', 'Duo'), sub: t('sp_golfer_count_2', '2 golfers, 1 shared room') },
                  { n: 3, icon: '🧑🧑🧑', label: t('sp_golfer_trio', 'Trio'), sub: t('sp_golfer_count_3', '3 golfers, 1–2 rooms') },
                  { n: 4, icon: '🧑🧑🧑🧑', label: t('sp_golfer_foursome', 'Foursome'), sub: t('sp_golfer_count_4', '4 golfers, 1–2 rooms') },
                  { n: 5, icon: '5️⃣', label: t('sp_golfer_group', 'Group'), sub: t('sp_golfer_count_5', '5 golfers, 2–3 rooms') },
                  { n: 6, icon: '6️⃣', label: t('sp_golfer_group', 'Group'), sub: t('sp_golfer_count_6', '6 golfers, 3 rooms') },
                  { n: 7, icon: '7️⃣', label: t('sp_golfer_group', 'Group'), sub: t('sp_golfer_count_7', '7 golfers, 3–4 rooms') },
                  { n: 8, icon: '8️⃣', label: t('sp_golfer_two_foursomes', 'Two foursomes'), sub: t('sp_golfer_count_8', '8 golfers, 4 rooms') },
                ].map(opt => {
                  const active = Number(pkgForm.NumGolfers) === opt.n
                  return (
                    <button key={opt.n} type="button"
                      onClick={() => setField('NumGolfers', opt.n)}
                      style={{
                        padding: '14px 8px',
                        border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                        borderRadius: 10,
                        background: active ? 'var(--brand-50)' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 22, marginBottom: 4, letterSpacing: -2 }}>{opt.icon}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                        {active ? '● ' : ''}{opt.n} {opt.n === 1 ? t('sp_golfer_cap', 'Golfer') : t('sp_golfers_cap', 'Golfers')}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                        {opt.sub}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Q2: How many rounds per golfer? */}
            <div style={{ marginBottom: 16 }}>
              <label style={fieldLabelStyle}>{t('sp_how_many_rounds', 'How many rounds does each golfer get?')}</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {[1, 2, 3].map(n => {
                  const active = Number(pkgForm.Rounds) === n
                  return (
                    <button key={n} type="button"
                      onClick={() => setField('Rounds', n)}
                      style={{
                        padding: '12px 8px',
                        border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                        borderRadius: 8,
                        background: active ? 'var(--brand-50)' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                        {active ? '● ' : ''}{n} {n !== 1 ? t('sp_rounds_cap', 'Rounds') : t('sp_round_cap', 'Round')}
                      </div>
                    </button>
                  )
                })}
                {(() => {
                  const active = Number(pkgForm.Rounds) > 3
                  return (
                    <button type="button"
                      onClick={() => setField('Rounds', Number(pkgForm.Rounds) > 3 ? pkgForm.Rounds : 4)}
                      style={{
                        padding: '12px 8px',
                        border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                        borderRadius: 8,
                        background: active ? 'var(--brand-50)' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                        {active ? `✎ ${pkgForm.Rounds} ${t('sp_rounds_cap', 'Rounds')}` : t('sp_custom', 'Custom')}
                      </div>
                    </button>
                  )
                })()}
              </div>
              {Number(pkgForm.Rounds) > 3 && (
                <input type="number" min="4" max="10"
                  value={pkgForm.Rounds}
                  onChange={e => {
                    const v = Math.min(Math.max(Number(e.target.value) || 4, 1), 10)
                    setField('Rounds', v)
                  }}
                  style={{ ...textInputStyle, marginTop: 8 }} />
              )}
            </div>

            {/* Q3: How many nights? */}
            <div style={{ marginBottom: 16 }}>
              <label style={fieldLabelStyle}>{t('sp_how_many_nights', 'How many nights?')}</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {[1, 2, 3].map(n => {
                  const active = Number(pkgForm.Nights) === n
                  return (
                    <button key={n} type="button"
                      onClick={() => setField('Nights', n)}
                      style={{
                        padding: '12px 8px',
                        border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                        borderRadius: 8,
                        background: active ? 'var(--brand-50)' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                        {active ? '● ' : ''}{n} {n !== 1 ? t('sp_nights_cap', 'Nights') : t('sp_night_cap', 'Night')}
                      </div>
                    </button>
                  )
                })}
                {(() => {
                  const active = Number(pkgForm.Nights) > 3
                  return (
                    <button type="button"
                      onClick={() => setField('Nights', Number(pkgForm.Nights) > 3 ? pkgForm.Nights : 4)}
                      style={{
                        padding: '12px 8px',
                        border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                        borderRadius: 8,
                        background: active ? 'var(--brand-50)' : '#fff',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                        {active ? `✎ ${pkgForm.Nights} ${t('sp_nights_cap', 'Nights')}` : t('sp_custom', 'Custom')}
                      </div>
                    </button>
                  )
                })()}
              </div>
              {Number(pkgForm.Nights) > 3 && (
                <input type="number" min="4" max="14"
                  value={pkgForm.Nights}
                  onChange={e => {
                    const v = Math.min(Math.max(Number(e.target.value) || 4, 1), 14)
                    setField('Nights', v)
                  }}
                  style={{ ...textInputStyle, marginTop: 8 }} />
              )}
            </div>

            {/* Summary callout */}
            <div style={{
              padding: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 20,
              display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: 13, color: '#166534', flex: 1 }}>
                <strong>{t('sp_this_package_books', 'This package books:')}</strong>{' '}
                {Number(pkgForm.NumGolfers) || 1} {Number(pkgForm.NumGolfers) > 1 ? t('sp_golfers_lower', 'golfers') : t('sp_golfer_lower', 'golfer')},{' '}
                {Number(pkgForm.Rounds) || 1} {Number(pkgForm.Rounds) > 1 ? t('sp_rounds_each_plural', 'rounds each') : t('sp_round_each_singular', 'round each')}{' '}
                ({totalRounds} {totalRounds !== 1 ? t('sp_tee_times_total_plural', 'tee times total') : t('sp_tee_time_total_singular', 'tee time total')}),{' '}
                {Number(pkgForm.Nights) || 1} {Number(pkgForm.Nights) > 1 ? t('sp_nights_lower', 'nights') : t('sp_night_lower', 'night')}.
              </div>
            </div>

            {/* Pricing */}
            <h3 style={sectionHeaderStyle}>{t('sp_pricing', 'Pricing')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={fieldLabelStyle}>{t('sp_package_price_total', 'Package price (total)')} <Req /></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: 'var(--text-muted)' }}>$</span>
                  <input type="number" min="1" step="0.01" value={pkgForm.Price}
                    onChange={e => setField('Price', e.target.value)}
                    placeholder="299.00"
                    style={{ ...textInputStyle, paddingLeft: 28 }} />
                </div>
                {perPerson && Number(pkgForm.NumGolfers) > 1 && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {t('sp_thats_per_person_pre', 'That\'s')} ${perPerson} {t('sp_per_person_for', 'per person for')} {Number(pkgForm.NumGolfers)} {t('sp_golfers_lower', 'golfers')}
                  </div>
                )}
              </div>
              <div>
                <label style={fieldLabelStyle}>{t('sp_max_hotel_budget', 'Max hotel budget per night ($)')}</label>
                <input type="number" min="0" step="1" value={pkgForm.MaxHotelBudget}
                  onChange={e => setField('MaxHotelBudget', e.target.value)}
                  style={textInputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={fieldLabelStyle}>{t('sp_num_hotel_rooms', 'Number of hotel rooms')}</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, maxWidth: 480 }}>
                  {[1, 2, 3].map(n => {
                    const active = Number(pkgForm.HotelRooms) === n
                    return (
                      <button key={n} type="button"
                        onClick={() => setField('HotelRooms', n)}
                        style={{
                          padding: '12px 8px',
                          border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                          borderRadius: 8,
                          background: active ? 'var(--brand-50)' : '#fff',
                          cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                        }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                          {active ? '● ' : ''}{n} {n !== 1 ? t('sp_rooms_cap', 'Rooms') : t('sp_room_cap', 'Room')}
                        </div>
                      </button>
                    )
                  })}
                  {(() => {
                    const active = Number(pkgForm.HotelRooms) > 3
                    return (
                      <button type="button"
                        onClick={() => setField('HotelRooms', Number(pkgForm.HotelRooms) > 3 ? pkgForm.HotelRooms : 4)}
                        style={{
                          padding: '12px 8px',
                          border: '2px solid ' + (active ? 'var(--brand-500)' : 'var(--card-border)'),
                          borderRadius: 8,
                          background: active ? 'var(--brand-50)' : '#fff',
                          cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                        }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
                          {active ? `✎ ${pkgForm.HotelRooms} ${t('sp_rooms_cap', 'Rooms')}` : t('sp_custom', 'Custom')}
                        </div>
                      </button>
                    )
                  })()}
                </div>
                {Number(pkgForm.HotelRooms) > 3 && (
                  <input type="number" min="4" max="10"
                    value={pkgForm.HotelRooms}
                    onChange={e => {
                      const v = Math.min(Math.max(Number(e.target.value) || 4, 1), 10)
                      setField('HotelRooms', v)
                    }}
                    style={{ ...textInputStyle, marginTop: 8, maxWidth: 200 }} />
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  {t('sp_hotel_rooms_hint', 'How many rooms the package books for the stay (e.g. 4 golfers in 2 rooms).')}
                </div>
              </div>
            </div>

            {/* Live cost breakdown + margin warning */}
            {(() => {
              const price = Number(pkgForm.Price) || 0
              const nights = Number(pkgForm.Nights) || 1
              const rooms = Number(pkgForm.HotelRooms) || 1
              const numGolfers = Number(pkgForm.NumGolfers) || 1
              const roundsPerGolfer = Number(pkgForm.Rounds) || 1
              const totalRounds = numGolfers * roundsPerGolfer
              const hotelBudget = Number(pkgForm.MaxHotelBudget) || 0
              const totalHotel = hotelBudget * nights * rooms
              const commission = Math.round(price * commissionPct) / 100

              // Multi-course: factor in partner course round costs (worst case).
              // Some partners use dynamic pricing, so snapshot base rates can't be trusted.
              // Upper-bound estimate uses MaxRoundPrice (the cap the creator committed to),
              // which is the actual ceiling a customer can book at.
              // Total rounds = NumGolfers × Rounds-per-golfer (e.g. 4 golfers × 4 rounds = 16 rounds).
              const isMulti = packageScope === 'multi'
              const maxPartnerRate = isMulti && multiRules.MaxRoundPrice ? Number(multiRules.MaxRoundPrice) : 0
              const partnerCost = isMulti ? totalRounds * maxPartnerRate : 0

              const remaining = price - totalHotel - commission - partnerCost
              if (!price) return null
              const isNegative = remaining < 0
              return (
                <div style={{
                  padding: 14, borderRadius: 8, marginBottom: 14,
                  background: isNegative ? '#fef2f2' : '#f8fafc',
                  border: '1px solid ' + (isNegative ? '#fecaca' : 'var(--card-border)'),
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--secondary-950)', marginBottom: 8 }}>
                    {t('sp_cost_breakdown', 'Cost breakdown')}
                  </div>
                  <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{t('sp_package_price_label', 'Package price')}</span>
                      <strong>${price.toFixed(2)}</strong>
                    </div>
                    {hotelBudget > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {t('sp_max_hotel_cost_breakdown', 'Max hotel cost')} ({rooms} {rooms !== 1 ? t('sp_rooms_lower', 'rooms') : t('sp_room_lower', 'room')} × {nights} {nights !== 1 ? t('sp_nights_lower', 'nights') : t('sp_night_lower', 'night')} × ${hotelBudget}/{t('sp_night_lower', 'night')})
                        </span>
                        <strong style={{ color: '#dc2626' }}>-${totalHotel.toFixed(2)}</strong>
                      </div>
                    )}
                    {isMulti && partnerCost > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {t('sp_max_partner_rounds_breakdown', 'Max partner course rounds')} ({numGolfers} {numGolfers !== 1 ? t('sp_golfers_lower', 'golfers') : t('sp_golfer_lower', 'golfer')} × {roundsPerGolfer} {roundsPerGolfer !== 1 ? t('sp_rounds_lower', 'rounds') : t('sp_round_lower', 'round')} = {totalRounds} × ${maxPartnerRate.toFixed(0)}/{t('sp_round_lower', 'round')})
                        </span>
                        <strong style={{ color: '#dc2626' }}>-${partnerCost.toFixed(2)}</strong>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{t('sp_integolf_commission', 'InteGolf commission')} ({commissionPct}%)</span>
                      <strong style={{ color: '#dc2626' }}>-${commission.toFixed(2)}</strong>
                    </div>
                    <div style={{ borderTop: '1px solid ' + (isNegative ? '#fca5a5' : 'var(--card-border)'), paddingTop: 6, marginTop: 2, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 600, color: isNegative ? '#dc2626' : '#166534' }}>
                        {t('sp_estimated_course_revenue', 'Estimated course revenue')}
                      </span>
                      <strong style={{ color: isNegative ? '#dc2626' : '#166534' }}>
                        ${remaining.toFixed(2)}
                      </strong>
                    </div>
                  </div>
                  {isMulti && partnerCost > 0 && (
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                      {t('sp_worst_case_blurb', 'Worst case: every round at your max cap. Actual cost depends on which tee times the customer picks; many partners use dynamic pricing.')}
                    </div>
                  )}
                  {isMulti && !maxPartnerRate && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#92400e', background: '#fef3c7', padding: 6, borderRadius: 6, lineHeight: 1.4 }}>
                      {t('sp_set_max_price_per_round', 'Set a max price per round in the Rules step to see the partner course cost estimate.')}
                    </div>
                  )}
                  {isNegative && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span aria-hidden="true">⚠</span>
                      {t('sp_costs_exceed_warning', 'Warning: costs exceed the package price. You\'ll lose money at the upper bound — reduce hotel budget, partner round cost, or raise the package price.')}
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Hotel availability indicator */}
            <div style={{
              padding: 12, borderRadius: 8, marginBottom: 14,
              background: '#f0f9ff', border: '1px solid #bae6fd',
              fontSize: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: hotelCheck?.suggestion ? 6 : 0 }}>
                {hotelChecking ? (
                  <>
                    <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #0284c7', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <span style={{ color: '#0284c7', fontWeight: 600 }}>{t('sp_checking_expedia', 'Searching for hotels near your course...')}</span>
                    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
                  </>
                ) : hotelCheck ? (
                  <>
                    <span style={{
                      fontSize: 16,
                      color: hotelCheck.available > 2 ? '#16a34a' : hotelCheck.available > 0 ? '#ca8a04' : '#dc2626',
                    }}>
                      {hotelCheck.available > 2 ? '✓' : hotelCheck.available > 0 ? '⚠' : '✗'}
                    </span>
                    <span style={{
                      fontWeight: 600,
                      color: hotelCheck.available > 2 ? '#166534' : hotelCheck.available > 0 ? '#854d0e' : '#991b1b',
                    }}>
                      {hotelCheck.message}
                    </span>
                    {hotelCheck.total > 0 && (
                      <span style={{ color: '#64748b' }}>({hotelCheck.total} {t('sp_total_in_area', 'total in area')})</span>
                    )}
                  </>
                ) : (
                  <span style={{ color: '#64748b' }}>{t('sp_set_hotel_budget', 'Set a hotel budget to check availability near your course.')}</span>
                )}
              </div>
              {/* Price suggestion when few options */}
              {hotelCheck?.suggestion && !hotelCheck.suggestion.lowerStars && !hotelChecking && (
                <div style={{
                  padding: '8px 10px', marginTop: 4,
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6,
                  color: '#92400e', fontSize: 11,
                }}>
                  <strong>{t('sp_suggestion_label', 'Suggestion:')}</strong> {t('sp_increase_budget_to', 'Increase budget to')} <strong>${hotelCheck.suggestion.budget}/{t('sp_night_lower', 'night')}</strong> {t('sp_to_unlock', 'to unlock')} ~{hotelCheck.suggestion.wouldUnlock} {t('sp_options_lower', 'options')}.
                  {hotelCheck.suggestion.cheapest && (
                    <span style={{ marginLeft: 6 }}>
                      {t('sp_cheapest_nearby', 'Cheapest nearby:')} <strong>{hotelCheck.suggestion.cheapest.hotel}</strong> {t('at', 'at')} ${hotelCheck.suggestion.cheapest.nightly}/{t('sp_night_lower', 'night')}
                    </span>
                  )}
                  <button type="button" onClick={() => setField('MaxHotelBudget', hotelCheck.suggestion.budget)}
                    style={{
                      marginLeft: 8, padding: '3px 10px', fontSize: 11,
                      background: '#92400e', color: '#fff', border: 'none',
                      borderRadius: 4, cursor: 'pointer', fontWeight: 600,
                    }}>
                    {t('sp_use_dollar', 'Use')} ${hotelCheck.suggestion.budget}
                  </button>
                </div>
              )}
            </div>


            {/* Tee time window */}
            <h3 style={sectionHeaderStyle}>{t('sp_tee_time_window', 'Tee time window')}</h3>
            <div style={{ marginBottom: 14 }}>
              <select value={pkgForm.TeeTimeRange || 'any'} onChange={e => setField('TeeTimeRange', e.target.value)} style={{ ...textInputStyle, maxWidth: 300 }}>
                {TIME_RANGES.map(tr => <option key={tr.value} value={tr.value}>{tr.label}</option>)}
              </select>
            </div>
            {pkgForm.TeeTimeRange === 'custom' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={fieldLabelStyle}>{t('from', 'From')}</label>
                  <input type="time" value={pkgForm.TeeTimeFrom || ''} onChange={e => setField('TeeTimeFrom', e.target.value)} style={textInputStyle} />
                </div>
                <div>
                  <label style={fieldLabelStyle}>{t('sp_filter_to', 'To')}</label>
                  <input type="time" value={pkgForm.TeeTimeTo || ''} onChange={e => setField('TeeTimeTo', e.target.value)} style={textInputStyle} />
                </div>
              </div>
            )}

            {/* Validity */}
            <h3 style={sectionHeaderStyle}>{t('sp_validity_optional', 'Validity (optional)')}</h3>
            <p style={hintStyle}>{t('sp_validity_blurb', 'Leave blank to make this package always available.')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={fieldLabelStyle}>{t('sp_valid_from', 'Valid from')}</label>
                <input type="date" value={pkgForm.ValidFrom || ''} onChange={e => setField('ValidFrom', e.target.value)} style={textInputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>{t('sp_valid_to', 'Valid to')}</label>
                <input type="date" value={pkgForm.ValidTo || ''} onChange={e => setField('ValidTo', e.target.value)} style={textInputStyle} />
              </div>
            </div>

            {/* Inclusions */}
            <h3 style={sectionHeaderStyle}>{t('sp_whats_included', 'What\'s included')}</h3>
            <p style={hintStyle}>{t('sp_inclusions_blurb', 'Bullet points shown to golfers in the widget. Click a previous one to quick-add, or type your own.')}</p>

            {/* Quick-add from previously used inclusions across all packages */}
            {(() => {
              const currentNames = new Set((pkgForm.inclusions || []).map(i => i.ItemName.toLowerCase().trim()))
              const prev = []
              const seen = new Set()
              for (const pkg of packages) {
                for (const inc of (pkg.inclusions || [])) {
                  const key = inc.ItemName.toLowerCase().trim()
                  if (!seen.has(key) && !currentNames.has(key)) {
                    seen.add(key)
                    prev.push(inc.ItemName)
                  }
                }
              }
              if (!prev.length) return null
              return (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
                    {t('sp_quick_add_inclusions', 'Quick add from your other packages:')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {prev.map(name => (
                      <button key={name} type="button"
                        onClick={() => setPkgForm(f => ({ ...f, inclusions: [...(f.inclusions || []), { ItemName: name }] }))}
                        style={{
                          padding: '4px 12px', fontSize: 12,
                          background: '#f8fafc', color: 'var(--secondary-950)',
                          border: '1px solid var(--card-border)', borderRadius: 16,
                          cursor: 'pointer', fontWeight: 500,
                          transition: 'all 0.1s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--brand-50)'; e.currentTarget.style.borderColor = 'var(--brand-300)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = 'var(--card-border)' }}
                      >
                        + {name}
                      </button>
                    ))}
                  </div>
                </div>
              )
            })()}

            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={newInclusion} onChange={e => setNewInclusion(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addInclusion() } }}
                  placeholder={t('sp_inclusion_placeholder', 'e.g. Cart included')}
                  style={textInputStyle} />
                <button type="button" onClick={addInclusion} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
                  {t('sp_add', 'Add')}
                </button>
              </div>
            </div>
            {pkgForm.inclusions?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {pkgForm.inclusions.map((inc, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', background: '#f8fafc',
                    border: '1px solid var(--card-border)', borderRadius: 6, fontSize: 13,
                  }}>
                    <span style={{ color: 'var(--brand-600)', fontWeight: 700 }} aria-hidden="true">✓</span>
                    <span style={{ flex: 1 }}>{inc.ItemName}</span>
                    <button type="button" onClick={() => removeInclusion(i)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
                      aria-label={t('sp_remove_inclusion', 'Remove inclusion')}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Payment options (plan 463) */}
            <h3 style={sectionHeaderStyle}>{t('sp_payment_options', 'Payment options')}</h3>
            <div style={{ background: '#f8fafc', borderRadius: 6, padding: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Toggle
                  checked={!!pkgForm.EnableSplitPayments}
                  onChange={(next) => setField('EnableSplitPayments', next ? 1 : 0)}
                />
                <span>
                  <strong>{t('sp_split_payment_strong', 'Split payment across golfers')}</strong>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 13 }}>
                    {t('sp_split_payment_blurb', 'Primary pays their share; other golfers receive a payment link by email.')}
                  </span>
                </span>
              </div>
              {pkgForm.EnableSplitPayments ? (
                <div style={{ marginLeft: 26, marginBottom: 10, fontSize: 13 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {t('sp_secondary_deadline', 'Deadline for secondaries to pay:')}
                    <input type="number" min="24" max="168" step="1" value={pkgForm.SecondaryPaymentDeadlineHours}
                      onChange={e => setField('SecondaryPaymentDeadlineHours', e.target.value)}
                      style={{ width: 80, padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    {t('sp_hours_lower', 'hours')}
                    <span style={{ color: 'var(--text-muted)' }}>(24-168)</span>
                  </label>
                </div>
              ) : null}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Toggle
                  checked={!!pkgForm.EnableInstallments}
                  onChange={(next) => setField('EnableInstallments', next ? 1 : 0)}
                />
                <span>
                  <strong>{t('sp_installments_strong', 'Allow installment payments')}</strong>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 13 }}>
                    {t('sp_installments_blurb', 'Trips 30+ days out only. First payment covers hotel; remainder in monthly installments before tee-time-minus-30-days.')}
                  </span>
                </span>
              </div>
              {pkgForm.EnableInstallments ? (
                <div style={{ marginLeft: 26, fontSize: 13 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {t('sp_max_installments', 'Max installments allowed:')}
                    <input type="number" min="2" max="12" step="1" value={pkgForm.MaxInstallments}
                      onChange={e => setField('MaxInstallments', e.target.value)}
                      style={{ width: 60, padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    <span style={{ color: 'var(--text-muted)' }}>(2-12)</span>
                  </label>
                </div>
              ) : null}

              <hr style={{ border: 0, borderTop: '1px solid var(--card-border)', margin: '12px 0' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Toggle
                  checked={!!pkgForm.EnableLoyaltyDiscount}
                  onChange={(next) => setField('EnableLoyaltyDiscount', next ? 1 : 0)}
                />
                <span>
                  <strong>{t('sp_loyalty_discount_strong', 'Loyalty discount for repeat customers')}</strong>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 13 }}>
                    {t('sp_loyalty_blurb', 'Auto-applied when a customer with a PlayStay account books their N+1th trip.')}
                  </span>
                </span>
              </div>
              {pkgForm.EnableLoyaltyDiscount ? (
                <div style={{ marginLeft: 26, fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {t('sp_discount_label', 'Discount:')}
                    <input type="number" min="0" max="100" step="0.01" value={pkgForm.LoyaltyDiscountPct}
                      onChange={e => setField('LoyaltyDiscountPct', e.target.value)}
                      style={{ width: 70, padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    <span style={{ color: 'var(--text-muted)' }}>%</span>
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {t('sp_min_prior_trips', 'Min prior trips:')}
                    <input type="number" min="1" max="10" step="1" value={pkgForm.LoyaltyMinPriorTrips}
                      onChange={e => setField('LoyaltyMinPriorTrips', e.target.value)}
                      style={{ width: 60, padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    <span style={{ color: 'var(--text-muted)' }}>(1-10)</span>
                  </label>
                </div>
              ) : null}
            </div>

            {/* Add-ons / upsells (Feature 2) */}
            <h3 style={sectionHeaderStyle}>{t('sp_addons_optional_upsells', 'Add-ons (optional upsells)')}</h3>
            <div style={{ background: '#f8fafc', borderRadius: 6, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                {t('sp_addons_blurb', 'Customers can add these to their booking at checkout. Examples: cart rental, range balls, lessons, food & beverage credits.')}
              </div>
              {(pkgForm.addons || []).map((a, idx) => (
                <div key={idx} style={{ background: '#fff', border: '1px solid var(--card-border)', borderRadius: 6, padding: 10, marginBottom: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 60px 60px', gap: 8, alignItems: 'end' }}>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('sp_name_label', 'Name')}</label>
                      <input type="text" value={a.Name || ''} placeholder={t('sp_addon_name_placeholder', 'Cart rental')}
                        onChange={e => setPkgForm(f => ({ ...f, addons: f.addons.map((x, i) => i === idx ? { ...x, Name: e.target.value } : x) }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('sp_price_dollar', 'Price ($)')}</label>
                      <input type="number" min="0" step="0.01" value={(Number(a.PriceCents) || 0) / 100}
                        onChange={e => setPkgForm(f => ({ ...f, addons: f.addons.map((x, i) => i === idx ? { ...x, PriceCents: Math.round(Number(e.target.value) * 100) } : x) }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('sp_mode_label', 'Mode')}</label>
                      <select value={a.PriceMode || 'flat'}
                        onChange={e => setPkgForm(f => ({ ...f, addons: f.addons.map((x, i) => i === idx ? { ...x, PriceMode: e.target.value } : x) }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }}>
                        <option value="flat">{t('sp_mode_flat', 'Flat')}</option>
                        <option value="per_golfer">{t('sp_mode_per_golfer', 'Per golfer')}</option>
                        <option value="per_round">{t('sp_mode_per_round', 'Per round')}</option>
                        <option value="per_night">{t('sp_mode_per_night', 'Per night')}</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('sp_max_short', 'Max')}</label>
                      <input type="number" min="1" max="99" value={a.MaxQuantity || 1}
                        onChange={e => setPkgForm(f => ({ ...f, addons: f.addons.map((x, i) => i === idx ? { ...x, MaxQuantity: Number(e.target.value) || 1 } : x) }))}
                        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4 }} />
                    </div>
                    <div>
                      <button type="button" onClick={() => setPkgForm(f => ({ ...f, addons: f.addons.filter((_, i) => i !== idx) }))}
                        style={{ width: '100%', padding: '6px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 18 }}>×</button>
                    </div>
                  </div>
                  <input type="text" value={a.Description || ''} placeholder={t('sp_description_optional', 'Description (optional)')}
                    onChange={e => setPkgForm(f => ({ ...f, addons: f.addons.map((x, i) => i === idx ? { ...x, Description: e.target.value } : x) }))}
                    style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--card-border)', borderRadius: 4, marginTop: 6, fontSize: 12 }} />
                </div>
              ))}
              <button type="button"
                onClick={() => setPkgForm(f => ({ ...f, addons: [...(f.addons || []), { Name: '', Description: '', PriceCents: 0, PriceMode: 'flat', MaxQuantity: 1, IsActive: 1 }] }))}
                style={{ padding: '8px 14px', background: '#fff', border: '1px dashed var(--card-border)', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
                {t('sp_add_an_addon', '+ Add an add-on')}
              </button>
            </div>

            {/* Active toggle */}
            <h3 style={sectionHeaderStyle}>{t('status', 'Status')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: '#f8fafc', borderRadius: 6, marginBottom: 24 }}>
              <Toggle
                checked={!!pkgForm.IsActive}
                onChange={(next) => setField('IsActive', next ? 1 : 0)}
              />
              <span>
                <strong>{t('active', 'Active')}</strong>
                <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 13 }}>
                  {t('sp_show_in_widget', 'Show this package in the widget')}
                </span>
              </span>
            </div>

            {/* Save bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--card-border)', paddingTop: 20 }}>
              <button type="button" onClick={cancelEditing} className="btn btn-outline-dark" style={{ padding: '12px 24px' }}>
                {t('cancel', 'Cancel')}
              </button>
              <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary"
                style={{ padding: '14px 32px', opacity: saving ? 0.5 : 1 }}>
                {saving ? t('sp_saving', 'Saving...') : (editing === 'new' ? t('sp_create_package', 'Create package') : t('sp_save_changes', 'Save changes'))}
              </button>
            </div>
          </div>
        )}
      </div>
    </PortalShell>
  )
}

function Req() {
  return <span style={{ color: '#dc2626' }} aria-label="required">*</span>
}

// ─── Styles ───
const errorBoxStyle = {
  padding: '10px 14px', marginBottom: 16,
  background: '#fef2f2', color: '#dc2626',
  border: '1px solid #fecaca', borderRadius: 8, fontSize: 13,
}
const sectionHeaderStyle = {
  margin: '20px 0 10px', fontSize: 14, color: 'var(--secondary-950)',
  fontWeight: 700, paddingBottom: 6, borderBottom: '1px solid var(--card-border)',
}
const fieldLabelStyle = {
  display: 'block', fontSize: 12, color: 'var(--text-muted)',
  marginBottom: 4, fontWeight: 600,
}
const hintStyle = {
  margin: '-8px 0 12px', fontSize: 12, color: 'var(--text-muted)',
}
const textInputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 6,
  border: '1px solid var(--card-border)', fontSize: 13,
  boxSizing: 'border-box',
}
const smallBtnStyle = {
  padding: '6px 14px', borderRadius: 6, border: '1px solid var(--card-border)',
  background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  whiteSpace: 'nowrap',
}
const golferBadge = {
  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
  background: '#dbeafe', color: '#1e40af', letterSpacing: 0.5,
}
const multiCourseBadge = {
  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
  background: '#fef3c7', color: '#92400e', letterSpacing: 0.5,
}
const recapEditBtn = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
  background: 'transparent', border: '1px solid var(--brand-500)',
  color: 'var(--brand-700)', cursor: 'pointer',
}
const inactiveBadge = {
  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
  background: '#f3f4f6', color: '#6b7280', letterSpacing: 0.5,
}
const inclusionPill = {
  fontSize: 11, padding: '3px 10px', background: '#f0fdf4',
  color: '#166534', border: '1px solid #bbf7d0', borderRadius: 12,
}
